import React, { useEffect, useState, Suspense, lazy } from 'react';
import Header from "components/Header/Header.js";
import HomeFooter from "components/Footer/HomeFooter.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { useSelector } from "react-redux";
import { Box, Typography, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Hero from "../components/Hero";
import PartnerUpsellPage from './PartnerUpsellPage';
import PartnerLanding from '../components/PartnerLanding.jsx';
import { getPartnerDisplayName, isPartnerComingSoon, isPartnerMaintenance, resolvePartnerFromHost } from '../common/partnerUtils';
import PartnerMaintenancePage from './PartnerMaintenancePage';
import PartnerComingSoonPage from './PartnerComingSoonPage';
import WebsiteMeta from '../components/WebsiteMeta.jsx';
import { getPartnerWebsiteContent, getWebsiteContent } from '../common/websiteUtils';
import { getDefaultSectionOrder, getHeroTheme, getSiteTheme, normalizeTemplateKey } from '../common/websiteTemplates';
import { buildPartnerNavigation } from '../common/navigationUtils';

const Section = lazy(() => import("../components/Section"));
const Download = lazy(() => import("../components/Download"));
const MapPreview = lazy(() => import("../components/MapPreview"));
const HowItWorks = lazy(() => import("../components/HowItWorks"));
const ValueProps = lazy(() => import("../components/ValueProps"));

const dashboardRoutes = [];

// Loading fallback component
const LoadingFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
    <CircularProgress size={32} sx={{ color: '#000' }} />
  </Box>
);

export default function LandingPage(props) {
  const { t, i18n } = useTranslation();
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const { ...rest } = props;
  const [role, setRole] = useState();
  const partnerFromProps = props.partner;
  const partnerFromHost = typeof window !== 'undefined' ? resolvePartnerFromHost(settings, window.location.hostname) : null;
  const partner = partnerFromProps || partnerFromHost;
  const isAdmin = auth?.profile?.usertype === 'admin' || auth?.profile?.usertype === 'fleetadmin';
  const bookingPath = props.bookingPath || (partnerFromHost ? '/book' : '/addbookings');
  const homePath = props.homePath || '/';
  const bookingState = partner ? { partner } : undefined;
  const mapCenter = partner && (partner.mapCenter || partner.center);
  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const storefrontParam = searchParams?.get('storefront');
  const forceStorefront =
    storefrontParam === '1' || storefrontParam === 'true' || storefrontParam === 'yes';
  const storefrontEnabled = Boolean(partner && (partner.storefrontEnabled || partner.storefront_enabled));
  const useStorefront = !partner ? true : forceStorefront || storefrontEnabled;

  const partnerOverrides = Boolean(partner && !useStorefront);
  const heroHeading = partnerOverrides && (partner.headline || getPartnerDisplayName(partner, settings));
  const heroSubheading = partnerOverrides && ((partner.description || partner.tagline) || partner.subtitle);
  const heroImage = partnerOverrides && (partner.heroImage || partner.bannerImage || partner.backgroundImage);
  const heroCtaLabel = partnerOverrides && partner.ctaLabel;
  const brandName = partnerOverrides && (partner.brandName || getPartnerDisplayName(partner, settings));
  const brandLogo = partnerOverrides && (partner.logoUrl || partner.logo || partner.logoURL);

  const websiteContent =
    partner && useStorefront
      ? getPartnerWebsiteContent(settings, partner, i18n.language)
      : getWebsiteContent(settings, i18n.language);

  const previewTemplate = typeof window !== 'undefined' && (!partner || useStorefront)
    ? searchParams?.get('previewTemplate')
    : null;
  const storedTemplateKey = normalizeTemplateKey(websiteContent.template || 'classic');
  const templateKey = previewTemplate ? normalizeTemplateKey(previewTemplate) : storedTemplateKey;

  const heroTheme = getHeroTheme(templateKey);
  const appearance = websiteContent.appearance || {};
  const siteTheme = getSiteTheme(templateKey, appearance);

  const headerColor = siteTheme.headerColor || 'dark';
  const headerScrollColor = siteTheme.headerScrollColor || headerColor;
  const navMode = siteTheme.navMode || (headerColor === 'white' ? 'light' : 'dark');

  const sectionOrder = Array.isArray(websiteContent.sectionOrder) && websiteContent.sectionOrder.length && !previewTemplate
    ? websiteContent.sectionOrder
    : getDefaultSectionOrder(templateKey);
  const contentBrandName = websiteContent.branding?.brandName || settings?.CompanyName || settings?.appName;
  const footerBrandName = websiteContent.branding?.brandName || settings?.CompanyName || settings?.appName;
  const navigation = buildPartnerNavigation(websiteContent.navigation || {}, { homePath, bookingPath }) || {};
  const seo = websiteContent.seo || {};

  useEffect(() => {
    if (auth && auth.profile) {
      if (auth.profile.uid) {
        let userRole = auth.profile.usertype;
        if (userRole === 'admin' || userRole === 'fleetadmin' || userRole === 'customer') {
          setRole('customer');
        } else if (userRole === 'driver') {
          setRole('driver');
        }
      }
    }
  }, [auth]);

  if (partner && partner.inactive) {
    return <PartnerUpsellPage partner={partner} />;
  }

  if (partner && isPartnerComingSoon(partner) && !isAdmin) {
    return <PartnerComingSoonPage partner={partner} />;
  }

  if (partner && isPartnerMaintenance(partner) && !isAdmin) {
    return <PartnerMaintenancePage partner={partner} />;
  }

  const renderSection = (key) => {
    switch (key) {
      case 'hero':
        if (websiteContent.enabledSections?.hero === false) return null;
        return (
          <Hero
            key="hero"
            role={role}
            bookingPath={bookingPath}
            bookingState={bookingState}
            heading={heroHeading || websiteContent.hero?.title}
            subheading={heroSubheading || websiteContent.hero?.subtitle}
            heroImage={heroImage || websiteContent.hero?.image}
            primaryCtaLabel={websiteContent.hero?.primaryCtaLabel}
            secondaryCtaLabel={heroCtaLabel || websiteContent.hero?.secondaryCtaLabel}
            showDriverCard={websiteContent.hero?.showDriverCard}
            driverCardTitle={websiteContent.hero?.driverCardTitle}
            driverCardSubtitle={websiteContent.hero?.driverCardSubtitle}
            heroTheme={heroTheme}
            templateKey={templateKey}
          />
        );
      case 'map':
        if (websiteContent.enabledSections?.map === false) return null;
        return (
          <React.Fragment key="map">
            <Box sx={{ bgcolor: siteTheme.sectionBg, pt: { xs: 6, md: 8 }, pb: { xs: 2, md: 4 } }}>
              <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 4 } }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: 28, md: 36 },
                    color: siteTheme.text,
                    mb: { xs: 3, md: 4 },
                    fontFamily: '"UberMove", -apple-system, sans-serif',
                  }}
                >
                  {websiteContent.map?.title || t('drivers_near_you') || 'Drivers near you'}
                </Typography>
              </Box>
            </Box>
            <Suspense fallback={<LoadingFallback />}>
              <MapPreview center={mapCenter} />
            </Suspense>
          </React.Fragment>
        );
      case 'howItWorks':
        if (websiteContent.enabledSections?.howItWorks === false) return null;
        return (
          <Suspense key="howItWorks" fallback={<LoadingFallback />}>
            <HowItWorks
              title={websiteContent.howItWorks?.title}
              steps={websiteContent.howItWorks?.steps}
              theme={siteTheme}
            />
          </Suspense>
        );
      case 'valueProps':
        if (websiteContent.enabledSections?.valueProps === false) return null;
        return (
          <Suspense key="valueProps" fallback={<LoadingFallback />}>
            <ValueProps
              cards={websiteContent.valueProps?.cards}
              safety={websiteContent.valueProps?.safety}
              theme={siteTheme}
            />
          </Suspense>
        );
      case 'driverBusiness':
        if (websiteContent.enabledSections?.driverBusiness === false) return null;
        return (
          <Suspense key="driverBusiness" fallback={<LoadingFallback />}>
            <Section driver={websiteContent.driver} business={websiteContent.business} theme={siteTheme} />
          </Suspense>
        );
      case 'download':
        if (websiteContent.enabledSections?.download === false) return null;
        return (
          <Suspense key="download" fallback={<LoadingFallback />}>
            <Download
              title={websiteContent.download?.title}
              subtitle={websiteContent.download?.subtitle}
              showApple={websiteContent.download?.showApple}
              showAndroid={websiteContent.download?.showAndroid}
              theme={siteTheme}
            />
          </Suspense>
        );
      default:
        return null;
    }
  };

  return (
    <div style={{ backgroundColor: partner && !useStorefront ? '#FFFFFF' : siteTheme.pageBg }}>
      {(!partner || useStorefront) && (
        <WebsiteMeta
          title={
            seo.title ||
            (partner ? partner.pageTitle || getPartnerDisplayName(partner, settings) : settings?.CompanyName) ||
            'Plukk'
          }
          description={seo.description || (partner ? partner.metaDescription : undefined)}
          image={seo.ogImage || (partner ? partner.ogImage : undefined)}
          favicon={
            websiteContent.branding?.faviconUrl || (partner ? partner.faviconUrl : settings?.favicon) || ''
          }
        />
      )}
      {/* Skip to content link for accessibility */}
      <a
        href="#maincontent"
        style={{
          position: 'absolute',
          left: -9999,
          top: 'auto',
          width: 1,
          height: 1,
          overflow: 'hidden',
        }}
        onFocus={(e) => {
          e.currentTarget.style.left = '8px';
          e.currentTarget.style.width = 'auto';
          e.currentTarget.style.height = 'auto';
          e.currentTarget.style.padding = '8px 12px';
          e.currentTarget.style.background = '#000';
          e.currentTarget.style.color = '#fff';
          e.currentTarget.style.zIndex = 1200;
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
          e.currentTarget.style.width = '1px';
          e.currentTarget.style.height = '1px';
          e.currentTarget.style.padding = '0';
          e.currentTarget.style.background = 'transparent';
        }}
      >
        Skip to content
      </a>

      <Header
        color={headerColor}
        navMode={navMode}
        routes={dashboardRoutes}
        rightLinks={<HeaderLinks homePath={homePath} bookingPath={bookingPath} navigation={navigation} />}
        brandName={brandName || contentBrandName}
        logoUrl={brandLogo || websiteContent.branding?.logoUrl}
        brandHref={homePath}
        fixed
        changeColorOnScroll={{
          height: 400,
          color: headerScrollColor,
        }}
        {...rest}
      />

      <main id="maincontent">
        {partner && !useStorefront ? (
          <PartnerLanding partner={partner} bookingPath={bookingPath} />
        ) : (
          <>
            {sectionOrder.map(renderSection)}
          </>
        )}
      </main>

      <HomeFooter navigation={navigation} brandName={footerBrandName} theme={siteTheme} />
    </div>
  );
}
